import React from 'react';

import colorsVars from 'styles/colors.module.scss';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { ActivityCell } from 'components/common/Tables/Cells/ActivityCell/ActivityCell';
import { ActivityIdCell } from 'components/common/Tables/Cells/ActivityIdCell/ActivityIdCell';
import { AssigneeCell } from 'components/common/Tables/Cells/AssigneeCell/AssigneeCell';
import { BasicDateCell } from 'components/common/Tables/Cells/BasicDateCell/BasicDateCell';
import { CriticalPathCell } from 'components/common/Tables/Cells/CriticalPathCell/CriticalPathCell';
import { CriticalityScoreCell } from 'components/common/Tables/Cells/CriticalityScoreCell/CriticalityScoreCell';
import { DateCell } from 'components/common/Tables/Cells/DateCell/DateCell';
import { DetailsCell } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';
import { DurationCell } from 'components/common/Tables/Cells/DurationCell/DurationCell';
import { FloatCell } from 'components/common/Tables/Cells/FloatCell/FloatCell';
import { IndicatorsCell } from 'components/common/Tables/Cells/IndicatorsCell/IndicatorsCell';
import { PredecessorsCell } from 'components/common/Tables/Cells/PredecessorsCell/PredecessorsCell';
import { StatusCell } from 'components/common/Tables/Cells/StatusCell/StatusCell';
import { SuccessorsCell } from 'components/common/Tables/Cells/SuccessorsCell/SuccessorsCell';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { VarianceCell } from 'components/common/Tables/Cells/VarianceCell/VarianceCell';
import { SubmittedDurationCompleteCell } from 'components/common/Tables/Cells/submittedDurationCompleteCell/SubmittedDurationCompleteCell';
import { customSortWithNull } from 'components/common/Tables/Tables.utils';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { TrackedUntrackedActionCellWrapper } from 'components/common/TrackedUntrackedAction/trackedUntrackedActionCellWrapper/trackedUntrackedActionCellWrapper';

import { VersionCompareQueryKeys } from 'components/Dashboards/Project/Components/VersionCompare/VersionCompare.consts';

const { color6: orange, textColor } = colorsVars;

const invalidateKeys = VersionCompareQueryKeys;

export const initialState: ITableColumnState = {
    hiddenColumns: [
        'details',
        'predecessors',
        'successors',
        'lateFinish',
        'lateStart',
        'plannedStartDate',
        'plannedFinishDate',
        'actualStartDate',
        'actualFinishDate',
        'plannedDuration',
        'actualDuration',
        'remainingDuration',
        'completedDuration',
        'durationOverrun',
        'projectWeekStart',
        'projectWeekFinish',
        'projectMonthStart',
        'projectMonthFinish',
        'parentName',
        'grandParentName',
    ],
    sortBy: [{ id: 'activity' }],
};

export const columns = [
    {
        Header: 'Activity Name',
        accessor: 'latestVersion.activityName',
        ...columnsCommonConfig.activity,
        Cell: ({ row, toggleAllRowsSelected }) => {
            const activityName = row.original.latestVersion?.activityName;

            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;

            const isTracked = row.original.latestVersion?.cardBoardViewResponse?.isTracked;
            const taskVersionHashCode = row.original.latestVersion?.cardBoardViewResponse?.taskVersionHashCode;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;
            const { toggleRowSelected } = row;

            return (
                <ActivityCell
                    activityName={activityName}
                    taskId={taskId}
                    isVirtual={isVirtual}
                    toggleRowSelected={toggleRowSelected}
                    toggleAllRowsSelected={toggleAllRowsSelected}
                    trackedUntrackedActionCell={
                        <TrackedUntrackedActionCellWrapper
                            isTracked={isTracked}
                            taskVersionHashCode={taskVersionHashCode}
                            invalidateQueriesKey={invalidateKeys}
                        />
                    }
                />
            );
        },
    },
    {
        Header: 'Activity ID',
        accessor: 'latestVersion.activityId',
        ...columnsCommonConfig.activityId,
        Cell: ({ row }) => {
            const activityId = row.original.latestVersion?.activityId;
            return <ActivityIdCell activityId={activityId} />;
        },
    },
    {
        Header: 'Risk Indicators',
        accessor: 'latestVersion.activityName',
        ...columnsCommonConfig.riskIndicators,
        Cell: ({ row }) => {
            const cardIndicators = row.original.latestVersion?.cardBoardViewResponse?.cardIndicators;
            return <IndicatorsCell cardIndicators={cardIndicators} />;
        },
    },
    {
        Header: 'Status',
        accessor: 'latestVersion.cardBoardViewResponse.status.label',
        ...columnsCommonConfig.status,
        Cell: ({ row }) => {
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;
            const cardType = row.original.latestVersion?.cardBoardViewResponse?.type?.name;
            const category = row.original.latestVersion?.cardBoardViewResponse?.category;
            const isInterimChanged = row.original.latestVersion?.cardBoardViewResponse?.isInterimChanged;
            const status = row.original.latestVersion?.cardBoardViewResponse?.status;
            const originalStatus = row.original.latestVersion?.cardBoardViewResponse?.originalStatus;

            return (
                <StatusCell
                    status={status}
                    isVirtual={isVirtual}
                    taskId={taskId}
                    originalStatus={originalStatus}
                    invalidateQueriesKey={invalidateKeys}
                    cardType={cardType}
                    category={category}
                    isInterimChanged={isInterimChanged}
                />
            );
        },
    },
    {
        Header: 'Assignee',
        accessor: 'latestVersion.cardBoardViewResponse.assignee.firstName',
        ...columnsCommonConfig.assignee,
        Cell: ({ row }) => {
            const assignee = row.original.latestVersion?.cardBoardViewResponse?.assignee;
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;

            return (
                <AssigneeCell
                    assignee={assignee}
                    taskId={taskId}
                    invalidateQueriesKey={invalidateKeys}
                    isVirtual={isVirtual}
                />
            );
        },
    },
    {
        Header: '% Complete',
        ...columnsCommonConfig.submittedDurationComplete,
        accessor: 'latestVersion.cardBoardViewResponse.submittedDurationComplete',
        Cell: ({ value }) => {
            return <SubmittedDurationCompleteCell value={value} />;
        },
    },
    {
        Header: 'Activity Type',
        ...columnsCommonConfig.activityType,
        accessor: 'latestVersion.cardBoardViewResponse.taskActivityType',
        Cell: ({ value }) => <TooltipCellWithCopy text={value} />,
    },
    {
        Header: 'Details/Last Comment',
        accessor: 'latestVersion.cardBoardViewResponse.details',
        ...columnsCommonConfig.details,
        Cell: ({ row }) => {
            const { details, cardLastComment, id, isVirtual, isGenAiDetails } =
                row.original.latestVersion?.cardBoardViewResponse;
            return (
                <DetailsCell
                    details={details}
                    cardLastComment={cardLastComment}
                    id={id}
                    isVirtual={isVirtual}
                    invalidateQueriesKey={invalidateKeys}
                    isGenAiDetails={isGenAiDetails}
                />
            );
        },
    },
    {
        Header: 'Start Date',
        accessor: 'changeMap.startDate',
        ...columnsCommonConfig.startDate,
        Cell: ({ row }) => {
            return (
                <DateCell
                    latestVersionDate={row.original.latestVersion?.startDate}
                    changeMapDate={row.original.changeMap?.startDate}
                    comparedVersion={row.original.comparedVersion?.startDate}
                    isChangeMap={row.original.changeMap}
                />
            );
        },
    },
    {
        Header: 'Finish Date',
        accessor: 'changeMap.finishDate',
        ...columnsCommonConfig.finishDate,
        Cell: ({ row }) => {
            return (
                <DateCell
                    latestVersionDate={row.original.latestVersion?.finishDate}
                    changeMapDate={row.original.changeMap?.finishDate}
                    comparedVersion={row.original.comparedVersion?.finishDate}
                    isChangeMap={row.original.changeMap}
                />
            );
        },
    },
    {
        Header: 'Duration',
        accessor: 'changeMap.duration',
        ...columnsCommonConfig.duration,
        Cell: ({ row }) => {
            return (
                <DurationCell
                    changeMap={row.original.changeMap}
                    changeMapDuration={row.original.changeMap?.duration}
                    latestVersionDuration={row.original.latestVersion?.duration}
                    comparedVersionDuration={row.original.comparedVersion?.duration}
                />
            );
        },
    },
    {
        Header: 'Criticality Score',
        accessor: 'changeMap.criticalityScore',
        ...columnsCommonConfig.criticalityScore,
        Cell: ({ row }) => {
            return <CriticalityScoreCell row={row} />;
        },
        sortType: customSortWithNull,
    },
    {
        Header: 'Float',
        accessor: 'changeMap.float',
        ...columnsCommonConfig.float,
        Cell: ({ row }) => {
            return <FloatCell row={row} />;
        },
        sortType: customSortWithNull,
    },
    {
        Header: 'Critical Path',
        accessor: (row) =>
            row.latestVersion?.cardBoardViewResponse?.cardIndicators.isCriticalPath.isIndicatorOn ? 'Yes' : 'No',
        ...columnsCommonConfig.criticalPath,
        Cell: ({ row, value }) => {
            return <CriticalPathCell row={row} value={value} />;
        },
    },
    {
        Header: 'Pred.',
        accessor: 'changeMap.predecessors',
        ...columnsCommonConfig.predecessors,
        Cell: ({ row }) => {
            return <PredecessorsCell row={row} />;
        },
        sortType: customSortWithNull,
    },
    {
        Header: 'Succ.',
        accessor: 'changeMap.successors',
        ...columnsCommonConfig.successors,
        Cell: ({ row }) => {
            return <SuccessorsCell row={row} />;
        },
        sortType: customSortWithNull,
    },
    {
        Header: 'Finish Variance',
        ...columnsCommonConfig.lateFinish,
        accessor: 'latestVersion.cardBoardViewResponse.finishVariance',
        Cell: ({ value }) => {
            return <VarianceCell value={value} />;
        },
    },
    {
        Header: 'Start Variance',
        ...columnsCommonConfig.lateStart,
        accessor: 'latestVersion.cardBoardViewResponse.startVariance',
        Cell: ({ value }) => {
            return <VarianceCell value={value} />;
        },
    },
    {
        Header: 'Planned Start Date ',
        ...columnsCommonConfig.plannedStartDate,
        accessor: 'latestVersion.cardBoardViewResponse.plannedStartDate',
        Cell: ({ value }) => {
            return <BasicDateCell value={value} />;
        },
    },
    {
        Header: 'Planned Finish Date',
        ...columnsCommonConfig.plannedFinishDate,
        accessor: 'latestVersion.cardBoardViewResponse.plannedFinishDate',
        Cell: ({ value }) => {
            return <BasicDateCell value={value} />;
        },
    },
    {
        Header: 'Actual Start Date',
        ...columnsCommonConfig.actualStartDate,
        accessor: 'latestVersion.cardBoardViewResponse.actualStartDate',
        Cell: ({ value }) => {
            return <BasicDateCell value={value} />;
        },
    },
    {
        Header: 'Actual Finish Date',
        ...columnsCommonConfig.actualFinishDate,
        accessor: 'latestVersion.cardBoardViewResponse.actualFinishDate',
        Cell: ({ value }) => {
            return <BasicDateCell value={value} />;
        },
    },
    {
        Header: 'Planned Duration',
        ...columnsCommonConfig.plannedDuration,
        accessor: 'latestVersion.cardBoardViewResponse.plannedDurationInDays',
    },
    {
        Header: 'Actual Duration',
        ...columnsCommonConfig.actualDuration,
        accessor: 'latestVersion.cardBoardViewResponse.actualDurationInDays',
    },
    {
        Header: 'Remaining Duration',
        ...columnsCommonConfig.remainingDuration,
        accessor: 'latestVersion.cardBoardViewResponse.remainingDurationInDays',
    },
    {
        Header: 'Current Duration',
        ...columnsCommonConfig.completedDuration,
        accessor: 'latestVersion.cardBoardViewResponse.completedDurationInDays',
    },
    {
        Header: 'Current Duration Overrun',
        ...columnsCommonConfig.durationOverrun,
        accessor: 'latestVersion.cardBoardViewResponse.completedDurationOverrun',
        Cell: ({ value }) => {
            const color = value > 0 ? orange : textColor;
            return <span style={{ color }}>{value}</span>;
        },
    },
    {
        Header: 'Project Week Start ',
        ...columnsCommonConfig.projectWeekStart,
        accessor: 'latestVersion.cardBoardViewResponse.projectWeekStart',
    },
    {
        Header: 'Project Week Finish',
        ...columnsCommonConfig.projectWeekFinish,
        accessor: 'latestVersion.cardBoardViewResponse.projectWeekFinish',
    },
    {
        Header: 'Project Month Start',
        ...columnsCommonConfig.projectMonthStart,
        accessor: 'latestVersion.cardBoardViewResponse.projectMonthStart',
    },
    {
        Header: 'Project Month Finish',
        ...columnsCommonConfig.projectMonthFinish,
        accessor: 'latestVersion.cardBoardViewResponse.projectMonthFinish',
    },
    {
        Header: 'Immediate Parent Name ',
        ...columnsCommonConfig.parentName,
        accessor: 'latestVersion.cardBoardViewResponse.parentActivity',
        Cell: ({ value }) => <TooltipCellWithCopy text={value} />,
    },
    {
        Header: 'Grand Parent Name',
        ...columnsCommonConfig.grandParentName,
        accessor: 'latestVersion.cardBoardViewResponse.grandParentActivity',
        Cell: ({ value }) => <TooltipCellWithCopy text={value} />,
    },
];
